import React, { useEffect, useRef } from 'react';
import { extractColumnRefs, evaluateFormula, buildSubstitutedFormula } from './formulaParser';
import './calculationTooltip.css';

interface CalculationTooltipProps {
    isOpen: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement>;
    formula: string;
    rowData: Record<string, any>;
    labels?: Record<string, string>;
    valueType?: 'number' | 'accounting';
}

const formatValue = (val: number, valueType: 'number' | 'accounting'): string => {
    if (!isFinite(val)) return '∞';
    if (isNaN(val)) return '—';
    if (valueType === 'accounting') {
        const abs = Math.abs(val);
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(abs);
        return val < 0 ? `$(${formatted})` : `$${formatted}`;
    }
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(val);
};

const CalculationTooltip: React.FC<CalculationTooltipProps> = ({
    isOpen,
    onClose,
    anchorRef,
    formula,
    rowData,
    labels = {},
    valueType = 'number',
}) => {
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = () => onClose();
        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        return () => window.removeEventListener('scroll', handleScroll, { capture: true });
    }, [isOpen, onClose]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target as Node) &&
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, anchorRef]);

    useEffect(() => {
        if (isOpen && tooltipRef.current && anchorRef.current) {
            const anchorRect = anchorRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = anchorRect.bottom + 4;
            let left = anchorRect.left;

            if (top + tooltipRect.height > viewportHeight) top = anchorRect.top - tooltipRect.height - 4;
            if (left + tooltipRect.width > viewportWidth) left = viewportWidth - tooltipRect.width - 8;
            if (left < 8) left = 8;

            tooltipRef.current.style.top = `${top}px`;
            tooltipRef.current.style.left = `${left}px`;
        }
    }, [isOpen, anchorRef]);

    if (!isOpen) return null;

    const columnRefs = extractColumnRefs(formula);
    const result = evaluateFormula(formula, rowData);
    const substituted = buildSubstitutedFormula(formula, rowData);

    return (
        <div
            ref={tooltipRef}
            className="calcTooltip-container"
            onClick={e => { e.stopPropagation(); e.preventDefault(); }}
            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
        >
            <div className="calcTooltip-header">Column Breakdown</div>
            <hr className="calcTooltip-divider" />
            {columnRefs.map(colName => {
                const raw = Number(rowData[colName] ?? 0);
                const label = labels[colName] ?? colName;
                return (
                    <div key={colName} className="calcTooltip-row">
                        <span className="calcTooltip-label">{label}</span>
                        <span className="calcTooltip-value">{formatValue(isNaN(raw) ? 0 : raw, valueType)}</span>
                    </div>
                );
            })}
            <hr className="calcTooltip-divider" />
            <div className="calcTooltip-formulaSection">
                <div className="calcTooltip-formulaLabel">Formula</div>
                <div className="calcTooltip-formulaText">{substituted}</div>
                <div className="calcTooltip-result">= {formatValue(result, valueType)}</div>
            </div>
        </div>
    );
};

export default CalculationTooltip;
